import { ExternalLink } from "lucide-react";

/* ------------------------------------------------------------------
   Standard IAB sizes used across the site:
   - LeaderboardAd     728 × 90    (top of main column, below hero)
   - RectangleAd       300 × 250   (sidebar, mid-position)
   - SidebarTallAd     300 × 600   (sidebar, half-page, premium direct-sell)
   - SidebarStickyAd   300 × 250   (sidebar bottom, sticky on long pages)
   - SponsoredCategoryLabel — inline pill, no fixed size
   ------------------------------------------------------------------ */

const EmptyShell = ({ width, height, testid }) => (
  <div
    className="mx-auto rounded-xl bg-[#FAFAFA] text-[#6B7280] flex items-center justify-center text-center text-xs uppercase tracking-wider border border-[#E5E5E5]"
    style={{ width: "100%", maxWidth: width, height }}
    data-testid={testid}
  >
    Sponsored placement available
  </div>
);

export const LeaderboardAd = ({ ad, onAdClick }) => {
  if (!ad?.image_url && !ad?.sponsor_name) {
    return <EmptyShell width={728} height={90} testid="ad-leaderboard-empty" />;
  }
  // Self-serve advertisers upload a 728×90 IAB Leaderboard image expecting
  // it to render as the ad itself. If an image is present, we render it
  // as a full-width banner (matching what the /advertise portal preview
  // shows). Only fall back to the icon+text layout when no image was
  // uploaded (direct-sell text-only sponsor spots).
  if (ad.image_url) {
    return (
      <a
        href={ad.link_url || "#"}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={onAdClick}
        className="block mx-auto rounded-xl overflow-hidden card-soft relative"
        style={{ width: "100%", maxWidth: 728, height: 90 }}
        data-testid="ad-leaderboard"
        aria-label={`${ad.sponsor_name || "Sponsored"}${ad.tagline ? " — " + ad.tagline : ""}`}
      >
        <img
          src={ad.image_url}
          alt={ad.sponsor_name || "Sponsored"}
          className="w-full h-full object-cover"
        />
        <span className="absolute top-1 left-3 text-[10px] uppercase tracking-wider bg-white/85 text-[#6B7280] px-1.5 rounded">
          Sponsored
        </span>
      </a>
    );
  }
  return (
    <a
      href={ad.link_url || "#"}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={onAdClick}
      className="block mx-auto rounded-xl overflow-hidden card-soft"
      style={{ width: "100%", maxWidth: 728, height: 90 }}
      data-testid="ad-leaderboard"
    >
      <div className="relative h-full flex items-center gap-4 px-4">
        <span className="absolute top-1 left-3 text-[10px] uppercase tracking-wider text-[#6B7280]">
          Sponsored
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[#1A1A1A] truncate" style={{ fontWeight: 500 }}>
            {ad.sponsor_name}
          </div>
          <div className="text-sm text-[#6B7280] truncate">{ad.tagline}</div>
        </div>
        <ExternalLink size={16} className="text-[#E63946] flex-shrink-0" />
      </div>
    </a>
  );
};

export const RectangleAd = ({ ad, onAdClick }) => {
  if (!ad?.image_url && !ad?.sponsor_name) {
    return (
      <div
        className="card-soft text-center border border-[#E5E5E5] flex flex-col items-center justify-center mx-auto"
        style={{ width: "100%", maxWidth: 300, height: 250 }}
        data-testid="ad-rectangle-empty"
      >
        <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">
          Sponsored
        </div>
        <div className="text-xs text-[#6B7280]">Ad slot available · 300×250</div>
      </div>
    );
  }
  return (
    <a
      href={ad.link_url || "#"}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={onAdClick}
      className="block card-soft overflow-hidden mx-auto"
      style={{ width: "100%", maxWidth: 300, height: 250 }}
      data-testid="ad-rectangle"
    >
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] px-4 pt-2">
        Sponsored
      </div>
      {ad.image_url && (
        <img
          src={ad.image_url}
          alt={ad.sponsor_name}
          className="w-full object-cover mt-2"
          style={{ height: 170 }}
        />
      )}
      <div className="px-4 py-2">
        <div className="text-[#1A1A1A] truncate" style={{ fontWeight: 500 }}>
          {ad.sponsor_name}
        </div>
        <div className="text-xs text-[#6B7280] truncate">{ad.tagline}</div>
      </div>
    </a>
  );
};

export const SidebarTallAd = ({ ad }) => {
  if (!ad?.image_url && !ad?.sponsor_name) {
    return (
      <div
        className="card-soft text-center border border-[#E5E5E5] flex flex-col items-center justify-center mx-auto"
        style={{ width: "100%", maxWidth: 300, height: 600 }}
        data-testid="ad-sidebar-tall-empty"
      >
        <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">
          Sponsored
        </div>
        <div className="text-xs text-[#6B7280]">Half-page slot · 300×600</div>
      </div>
    );
  }
  return (
    <a
      href={ad.link_url || "#"}
      target="_blank"
      rel="noopener noreferrer sponsored"
      className="block card-soft overflow-hidden mx-auto"
      style={{ width: "100%", maxWidth: 300, height: 600 }}
      data-testid="ad-sidebar-tall"
    >
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] px-4 pt-2">
        Sponsored
      </div>
      {ad.image_url && (
        <img
          src={ad.image_url}
          alt={ad.sponsor_name}
          className="w-full object-cover mt-2"
          style={{ height: 520 }}
        />
      )}
      <div className="px-4 py-2">
        <div className="text-[#1A1A1A] truncate" style={{ fontWeight: 500 }}>
          {ad.sponsor_name}
        </div>
        <div className="text-xs text-[#6B7280] truncate">{ad.tagline}</div>
      </div>
    </a>
  );
};

export const SidebarStickyAd = ({ ad }) => {
  const inner = !ad?.image_url && !ad?.sponsor_name ? (
    <div
      className="card-soft text-center border border-[#E5E5E5] flex flex-col items-center justify-center mx-auto"
      style={{ width: "100%", maxWidth: 300, height: 250 }}
      data-testid="ad-sidebar-sticky-empty"
    >
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">
        Sponsored
      </div>
      <div className="text-xs text-[#6B7280]">Ad slot available · 300×250</div>
    </div>
  ) : (
    <a
      href={ad.link_url || "#"}
      target="_blank"
      rel="noopener noreferrer sponsored"
      className="block card-soft overflow-hidden mx-auto"
      style={{ width: "100%", maxWidth: 300, height: 250 }}
      data-testid="ad-sidebar-sticky"
    >
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] px-4 pt-2">
        Sponsored
      </div>
      {ad.image_url && (
        <img
          src={ad.image_url}
          alt={ad.sponsor_name}
          className="w-full object-cover mt-2"
          style={{ height: 170 }}
        />
      )}
      <div className="px-4 py-2">
        <div className="text-[#1A1A1A] truncate" style={{ fontWeight: 500 }}>
          {ad.sponsor_name}
        </div>
        <div className="text-xs text-[#6B7280] truncate">{ad.tagline}</div>
      </div>
    </a>
  );
  return (
    // top-36 (144px) clears both header states with visible breathing
    // room: expanded header = 128px, compressed = 112px. Previously
    // top-24 (96px) sat UNDER both states so the sticky ad's top edge
    // was clipped by the nav bar once it hit the sticky boundary.
    <div className="sticky top-36" data-testid="ad-sidebar-sticky-wrap">
      {inner}
    </div>
  );
};

export const SponsoredCategoryLabel = ({ ad }) => {
  if (!ad?.sponsor_name) return null;
  return (
    <span
      className="inline-block ml-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#E63946]/10 text-[#E63946]"
      data-testid="ad-sponsored-category"
    >
      Sponsored by {ad.sponsor_name}
    </span>
  );
};
